import { FlowType } from '@domain/types';
import { PayloadAction } from '@reduxjs/toolkit';
import electronService from '@services/electron/electron.service';
import { logger } from '@services/logger/logger.service';
import { getErrorCode } from '@services/logger/scrubFilePaths';
import { WatchOptions } from 'chokidar';
import { EventChannel, eventChannel } from 'redux-saga';
import { FileBatchDebouncer } from './fileBatchDebouncer';

const commonOptions: WatchOptions = {
  ignored: '**/.*',
  depth: 0,
  ignoreInitial: true,
};

function createFileWatchingChannel(config: Partial<WatchOptions> = {}): EventChannel<string[]> {
  return eventChannel((emit) => {
    if (!electronService.isElectronEnvironment()) {
      logger.error('DESKTOP', 'Cannot create file watching channel: not in electron environment');
      return () => {};
    }

    const debouncer = new FileBatchDebouncer();
    debouncer.setOnFilesReady((filePaths) => {
      if (filePaths.length === 0) {
        return;
      }

      logger.info('DESKTOP', `WATCHER: emitting batch to saga`, { count: filePaths.length });
      emit(filePaths);
    });

    let watcher: any;

    electronService
      .initFileWatcher({ ...commonOptions, ...config })
      .then((fileWatcher) => {
        watcher = fileWatcher;

        watcher
          .on('add', (filePath: string) => {
            logger.info('DESKTOP', 'WATCHER: new file detected');
            debouncer.onWatchFile(filePath);
          })
          .on('error', (error: Error) => {
            logger.error('DESKTOP', `Watcher error (${getErrorCode(error)})`, undefined, false);
          });
      })
      .catch((error) => {
        logger.error(
          'DESKTOP',
          `Failed to initialize file watcher (${getErrorCode(error)})`,
          undefined,
          false,
        );
      });

    return () => {
      if (!watcher) {
        return;
      }

      try {
        watcher.close();
      } catch (error) {
        logger.error(
          'DESKTOP',
          `Watcher error while closing (${getErrorCode(error)})`,
          undefined,
          false,
        );
      }
    };
  });
}

type ActionCallback = (...args: any) => PayloadAction<any>;

export interface WatcherChannel {
  callback: ActionCallback;
  channel: EventChannel<string[]>;
  name: FlowType;
}

export type WatcherChannelConfigs = Omit<WatcherChannel, 'channel'>[];

export function initFileWatchers(
  channelsToCreate: WatcherChannelConfigs,
  config: Partial<WatchOptions> = {},
): WatcherChannel[] {
  return channelsToCreate.map((channelConfig) => ({
    ...channelConfig,
    channel: createFileWatchingChannel(config),
  }));
}

export function closeWatchers(watchers: WatcherChannel[]): void {
  watchers.forEach((watcher) => watcher.channel.close());
}
